import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '@/contexts/AuthContext';
import { authService } from '@/services/auth.service';
import apiService from '@/services/api.service';
import { LoadingSpinner } from '@/components';

interface PublicSector {
  id: string;
  name: string;
}

const CompleteProfilePage: FC = () => {
  const { user, refreshUser } = useAuthContext();
  const navigate = useNavigate();

  const [sectors, setSectors] = useState<PublicSector[]>([]);
  const [sectorId, setSectorId] = useState('');
  const [name, setName] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // If user doesn't need profile completion, redirect
    if (user && !user.needs_profile_completion) {
      navigate('/', { replace: true });
      return;
    }

    // Pre-fill name from user data
    if (user?.name) {
      setName(user.name);
    }

    // Load sectors
    apiService
      .get<PublicSector[]>('/sectors/')
      .then((res) => {
        // Handle both paginated and non-paginated responses
        const data = Array.isArray(res.data) ? res.data : (res.data as unknown as { results: PublicSector[] }).results;
        setSectors(data);
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!sectorId) {
      setError('Selecione um setor');
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      await authService.completeProfile({
        sector_id: sectorId,
        name: name.trim() || undefined,
      });
      await refreshUser();
      navigate('/', { replace: true });
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      setError(axiosErr?.response?.data?.error || 'Erro ao completar perfil');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-surface-primary">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-primary px-4 py-12 sm:px-6 lg:px-8">
      <div className="w-full max-w-md space-y-8">
        <div>
          <h2 className="text-center text-2xl font-bold text-content-primary">
            Bem-vindo!
          </h2>
          <p className="mt-2 text-center text-sm text-content-secondary">
            Complete seu perfil para comecar a usar a plataforma
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-content-muted mb-1.5">
              Nome de exibicao
            </label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="block w-full rounded-lg border border-theme-border bg-surface-secondary px-3 py-2 text-content-primary placeholder-content-muted focus:border-accent focus:outline-none focus:ring-accent sm:text-sm"
              placeholder="Seu nome"
            />
          </div>

          <div>
            <label htmlFor="sector" className="block text-sm font-medium text-content-muted mb-1.5">
              Setor <span className="text-rose-400">*</span>
            </label>
            <select
              id="sector"
              value={sectorId}
              onChange={(e) => setSectorId(e.target.value)}
              className="block w-full rounded-lg border border-theme-border bg-surface-secondary px-3 py-2 text-content-primary focus:border-accent focus:outline-none focus:ring-accent sm:text-sm"
            >
              <option value="">Selecione seu setor</option>
              {sectors.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>

          {error && (
            <div className="rounded-lg bg-rose-500/10 border border-rose-500/20 px-4 py-3 text-sm text-rose-400">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={isSaving || !sectorId}
            className="flex w-full justify-center rounded-lg bg-accent px-3 py-2.5 text-sm font-semibold text-accent-contrast hover:bg-accent-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? 'Salvando...' : 'Completar Cadastro'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default CompleteProfilePage;
